import React, { useMemo } from 'react'

/**
 * Converts a React hook into a Higher-Order Component (HOC).
 *
 * The hook result is mapped (optionally) and injected into the wrapped
 * component as a prop with the specified name.
 *
 * @template P Props of the wrapped component.
 * @template H Hook result type.
 * @template M Mapped value type.
 *
 * @param {string} name - Name of the prop that will receive the hook result.
 * @param {(props: P) => H} hook - Hook to execute within the HOC.
 * @param {(result: H) => M} [mapper=(result) => result] - Optional function
 * that transforms the hook result before it is passed to the wrapped component.
 *
 * @returns {(Wrapped: React.ComponentType<P & Record<string, M>>) => React.ComponentType<P>}
 * A Higher-Order Component that injects the mapped hook result as a prop.
 *
 * @example
 * const withUser = useHookAsHoc(
 *   'user',
 *   useCurrentUser,
 *   (result) => result.data
 * );
 *
 * const Profile = ({ user }) => <div>{user.name}</div>;
 *
 * export default withUser(Profile);
 */
export const useHookAsHoc =
  (name, hook, mapper = (result) => result) =>
  (Wrapped) => {
    const Comp = (props) => {
      const result = hook(props)
      const mapped = useMemo(() => mapper(result), [result])
      return <Wrapped {...props} {...{ [name]: mapped }} />
    }

    Comp.displayName = `useHookAsHoc${hook.name}`

    return Comp
  }
